import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import config from '../../Config';
import EditSubCategoryForm from './EditSubCategoryForm';
import AddSubCategoryForm from './AddSubCategoryForm';

const CategoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [category, setCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSub, setEditingSub] = useState(null);
  const [addingSub, setAddingSub] = useState(false);
  const [products, setProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  const fetchCategoryDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBaseUrl}/admin/upsale/category/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success && data.category) {
        setCategory(data.category);
        const subs = data.subCategory || [];
        setSubCategories(subs);
      }
    } catch (err) {
      console.error('Network error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (subId = null) => {
    setProductsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (subId) queryParams.append('subCategory', subId);
      else queryParams.append('category', id);

      const response = await fetch(
        `${config.apiBaseUrl}/admin/upsale/product?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setProducts(data.products || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error(err);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryDetail();
  }, [id]);

  const handleEditClick = (sub) => setEditingSub(sub);

  const handleDeleteClick = async (sub) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete subcategory "${sub.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${config.apiBaseUrl}/admin/upsale/subcategory/delete/${sub._id}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();
        if (response.ok) {
          Swal.fire('Deleted!', data.message || 'Subcategory deleted successfully.', 'success');
          fetchCategoryDetail();
        } else {
          Swal.fire('Error', data.message || 'Failed to delete subcategory.', 'error');
        }
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Something went wrong.', 'error');
      }
    }
  };

  const handleSubClick = (sub) => {
    setSelectedSub(sub);
    setShowProducts(true);
    fetchProducts(sub._id);
  };

  const handleBackToSubcategories = () => {
    setShowProducts(false);
    setSelectedSub(null);
    setProducts([]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!category) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6">Category not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, py: 3, maxWidth: '1200px', mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Tooltip title="Go Back">
          <IconButton onClick={() => navigate(-1)} size="large" color="primary">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold">
          {category.name}
        </Typography>
      </Stack>

      {/* Category Details */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                width: '100%',
                height: 250,
                borderRadius: 2,
                boxShadow: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f0f0',
              }}
            >
              {category.image ? (
                <Box
                  component="img"
                  src={category.image}
                  alt={category.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', minWidth: '200px' }}
                />
              ) : (
                <Typography variant="subtitle1" color="text.secondary">
                  No Image Available
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {category.description || 'No description available.'}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Has Subcategory
                </Typography>
                <Chip
                  label={category.hasSubCategory ? 'Yes' : 'No'}
                  color={category.hasSubCategory ? 'success' : 'default'}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={category.isActive ? 'Active' : 'Inactive'}
                  color={category.isActive ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {addingSub ? (
        <AddSubCategoryForm
          category={category}
          onCancel={() => setAddingSub(false)}
          onSuccess={() => {
            setAddingSub(false);
            fetchCategoryDetail();
          }}
        />
      ) : editingSub ? (
        <EditSubCategoryForm
          subCategory={editingSub}
          categoryId={category._id}
          onCancel={() => setEditingSub(null)}
          onSuccess={() => {
            setEditingSub(null);
            fetchCategoryDetail();
          }}
        />
      ) : showProducts ? (
        <Box>
          <Box display="flex" justifyContent="flex-start" mb={2}>
            {subCategories.length > 0 && (
              <Button variant="outlined" onClick={handleBackToSubcategories}>
                Back to Subcategories
              </Button>
            )}
          </Box>

          {productsLoading ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.length > 0 ? (
                    products.map((prod) => (
                      <TableRow key={prod._id}>
                        <TableCell>
                          {prod.image ? (
                            <Box
                              component="img"
                              src={prod.image}
                              alt={prod.name}
                              sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No Image
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{prod.name}</TableCell>
                        <TableCell>{prod.description || 'No description'}</TableCell>
                        <TableCell>{prod.price?.$numberDecimal || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={prod.isActive ? 'Active' : 'Inactive'}
                            color={prod.isActive ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No product available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ) : (
        // Subcategory Table
        category.hasSubCategory && (
          <Box>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" color="primary" onClick={() => setAddingSub(true)}>
                Add Subcategory
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subCategories.length > 0 ? (
                    subCategories.map((sub) => (
                      <TableRow key={sub._id}>
                        <TableCell>
                          {sub.image ? (
                            <Box
                              component="img"
                              src={sub.image}
                              alt={sub.name}
                              sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                              No Image
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button onClick={() => handleSubClick(sub)}>{sub.name}</Button>
                        </TableCell>
                        <TableCell>{sub.description || 'No description available.'}</TableCell>
                        <TableCell>
                          <Chip
                            label={sub.isActive ? 'Active' : 'Inactive'}
                            color={sub.isActive ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditClick(sub)}>
                                <EditIcon color="primary" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDeleteClick(sub)}>
                                <DeleteIcon color="error" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No subcategory available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}
    </Box>
  );
};

export default CategoryDetail;
